import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Avatar,
  useTheme,
  alpha,
  LinearProgress,
  Drawer,
  FormControlLabel,
  Checkbox,
  Stack,
  Paper,
} from '@mui/material';
import { Edit, CheckCircle, Cancel, AddCircleOutline, TrendingUp, Business, FilterList, Close } from '@mui/icons-material';
import toast from 'react-hot-toast';
import useSWR from 'swr';

import { DEVELOPER_MODE } from 'src/constants/config';
import { expertApi } from 'src/api/expertApi';
import PageTemplate from 'src/components/PageTemplate';
import EditExpertModal from 'src/pages/Experts/components/EditExpertModal';
import AddExpertModal from 'src/pages/Experts/components/AddExpertModal';

const Experts = () => {
  const theme = useTheme();
  const { data, error, mutate, isLoading } = useSWR('experts', () => expertApi.getExperts(), { refreshInterval: 1000 });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [currentExpert, setCurrentExpert] = useState(null);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: {
      active: false,
      inactive: false,
    },
    poc: false,
    prompt: false,
  });

  const parsePointsOfContact = (jsonData) => {
    if (!jsonData || jsonData === '[object Object]') {
      return [];
    }
    try {
      return JSON.parse(jsonData);
    } catch (e) {
      return [];
    }
  };

  const filteredExperts = useMemo(() => {
    if (!data) return [];

    return data.filter((expert) => {
      const matchesStatus =
        (!filters.status.active && !filters.status.inactive) ||
        (filters.status.active && expert.isActive === 1) ||
        (filters.status.inactive && expert.isActive === 0);

      const matchesPoc = !filters.poc || parsePointsOfContact(expert.pointOfContactJson).length > 0;
      const matchesPrompt = !filters.prompt || expert.promptModify;

      return matchesStatus && matchesPoc && matchesPrompt;
    });
  }, [data, filters]);

  const groupedExperts = useMemo(() => {
    const normalizeCompanyName = (companyName) => {
      return companyName.replace(/\s+/g, '').toLowerCase();
    };

    const groupByCompany = (experts) => {
      const grouped = {};

      experts.forEach((expert) => {
        const normalizedKey = normalizeCompanyName(expert.company);

        if (!grouped[normalizedKey]) {
          grouped[normalizedKey] = {
            displayName: expert.company,
            experts: [],
          };
        }

        grouped[normalizedKey].experts.push(expert);
      });

      return grouped;
    };

    return groupByCompany(filteredExperts);
  }, [filteredExperts]);

  const stats = useMemo(() => {
    if (!filteredExperts?.length) return { total: 0, active: 0, withPrompt: 0 };
    return {
      total: filteredExperts.length,
      active: filteredExperts.filter((e) => e.isActive === 1).length,
      withPrompt: filteredExperts.filter((e) => e.promptModify).length,
    };
  }, [filteredExperts]);

  if (error) {
    toast.error('Error fetching data:', error);
    return <div>Failed to load queries</div>;
  }

  const handleEditClick = (expert) => {
    setCurrentExpert(expert);
    setIsEditModalOpen(true);
  };

  const handleSaveClick = async (expertId, updatedData) => {
    try {
      await expertApi.updateExpert(expertId, updatedData);
      mutate();
    } catch (err) {
      toast.error('Failed to update expert: ' + err.message);
    }
  };

  const statusIcon = (status) =>
    status === 1 ? (
      <Chip
        icon={<CheckCircle sx={{ fontSize: '0.875rem' }} />}
        label="Active"
        size="small"
        sx={{
          backgroundColor: alpha(theme.palette.success.main, 0.08),
          color: theme.palette.success.main,
          height: 24,
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: 1,
          '& .MuiChip-icon': {
            color: theme.palette.success.main,
            marginLeft: '4px',
          },
        }}
      />
    ) : (
      <Chip
        icon={<Cancel sx={{ fontSize: '0.875rem' }} />}
        label="Client offboarded"
        size="small"
        sx={{
          backgroundColor: alpha(theme.palette.error.main, 0.08),
          color: theme.palette.error.main,
          height: 24,
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: 1,
          '& .MuiChip-icon': {
            color: theme.palette.error.main,
            marginLeft: '4px',
          },
        }}
      />
    );

  const booleanStatusIcon = (status) =>
    status === 1 ? (
      <Chip
        icon={<CheckCircle sx={{ fontSize: '0.875rem' }} />}
        label="Yes"
        size="small"
        sx={{
          backgroundColor: alpha(theme.palette.success.main, 0.08),
          color: theme.palette.success.main,
          height: 24,
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: 1,
          '& .MuiChip-icon': {
            color: theme.palette.success.main,
            marginLeft: '4px',
          },
        }}
      />
    ) : (
      <Chip
        icon={<Cancel sx={{ fontSize: '0.875rem' }} />}
        label="No"
        size="small"
        sx={{
          backgroundColor: alpha(theme.palette.error.main, 0.08),
          color: theme.palette.error.main,
          height: 24,
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: 1,
          '& .MuiChip-icon': {
            color: theme.palette.error.main,
            marginLeft: '4px',
          },
        }}
      />
    );

  const handleFilterChange = (type, value) => {
    setFilters((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      status: {
        active: false,
        inactive: false,
      },
      poc: false,
      prompt: false,
    });
  };

  const FilterDrawer = () => (
    <Drawer
      anchor="right"
      open={isFilterDrawerOpen}
      onClose={() => setIsFilterDrawerOpen(false)}
      PaperProps={{
        sx: {
          width: 320,
          p: 3,
          background: theme.palette.background.paper,
        },
      }}
    >
      <Stack spacing={3}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
          <IconButton onClick={() => setIsFilterDrawerOpen(false)} size="small">
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
            Status
          </Typography>
          <Stack>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.status.active}
                  onChange={(e) => handleFilterChange('status', { ...filters.status, active: e.target.checked })}
                />
              }
              label="Active"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.status.inactive}
                  onChange={(e) => handleFilterChange('status', { ...filters.status, inactive: e.target.checked })}
                />
              }
              label="Client offboarded"
            />
          </Stack>
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
            Additional Filters
          </Typography>
          <Stack>
            <FormControlLabel
              control={<Checkbox checked={filters.poc} onChange={(e) => handleFilterChange('poc', e.target.checked)} />}
              label="Has Point of Contact"
            />
            <FormControlLabel
              control={<Checkbox checked={filters.prompt} onChange={(e) => handleFilterChange('prompt', e.target.checked)} />}
              label="Has Prompt"
            />
          </Stack>
        </Box>

        <Button variant="outlined" onClick={clearFilters} startIcon={<Close />} sx={{ mt: 2 }}>
          Clear Filters
        </Button>
      </Stack>
    </Drawer>
  );

  const StatCard = ({ title, value, total, icon: Icon, color }) => (
    <Card
      elevation={0}
      sx={{
        p: 2,
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${color} 400%)`,
        border: `1px solid ${color}`,
        borderRadius: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Icon sx={{ color: color, fontSize: '1.25rem' }} />
          <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" sx={{ color: theme.palette.text.primary, fontWeight: 700, mb: 1 }}>
          {value}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={(value / total) * 100}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: theme.palette.background.paper,
            '& .MuiLinearProgress-bar': {
              backgroundColor: color,
            },
          }}
        />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          right: -20,
          bottom: -20,
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${color} 0%, ${theme.palette.background.paper} 100%)`,
        }}
      />
    </Card>
  );

  StatCard.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    icon: PropTypes.elementType.isRequired,
    color: PropTypes.string.isRequired,
  };

  return (
    <PageTemplate loading={isLoading}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          mt: { xs: 10, sm: 4 },
          px: { xs: 0, sm: 1 },
          maxWidth: '1400px',
          mx: 'auto',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  letterSpacing: '-0.02em',
                }}
              >
                Experts Directory
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  mt: 0.5,
                }}
              >
                Manage and overview all expert profiles
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                width: { xs: '100%', md: 'auto' },
                justifyContent: { xs: 'space-between', md: 'flex-end' },
              }}
            >
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => setIsFilterDrawerOpen(true)}
                sx={{
                  borderColor: alpha(theme.palette.primary.main, 0.2),
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.background.paper,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                Filters
                {Object.values(filters.status).some(Boolean) || filters.poc || filters.prompt ? (
                  <Box
                    sx={{
                      ml: 1,
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                    }}
                  >
                    {Object.values(filters.status).filter(Boolean).length + (filters.poc ? 1 : 0) + (filters.prompt ? 1 : 0)}
                  </Box>
                ) : null}
              </Button>
              <Button
                variant="contained"
                onClick={() => setIsAddModalOpen(true)}
                startIcon={<AddCircleOutline />}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: 'white',
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  },
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                Add Expert
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 3,
            }}
          >
            <StatCard title="Total Experts" value={stats.total} total={stats.total} icon={Business} color={theme.palette.primary.main} />
            <StatCard title="Active Experts" value={stats.active} total={stats.total} icon={CheckCircle} color={theme.palette.success.main} />
            <StatCard title="With Prompts" value={stats.withPrompt} total={stats.total} icon={TrendingUp} color={theme.palette.info.main} />
          </Box>
        </Box>

        {Object.entries(groupedExperts).map(([normalizedKey, { displayName, experts }]) => (
          <Card
            key={normalizedKey}
            elevation={0}
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              backgroundColor: theme.palette.background.paper,
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
              },
              transition: 'all 0.3s ease',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
              }}
            />
            <CardContent sx={{ p: 0 }}>
              <Box
                sx={{
                  px: 3,
                  py: 2.5,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: alpha(theme.palette.background.default, 0.5),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      width: 40,
                      height: 40,
                    }}
                  >
                    <Business />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        fontSize: '1.1rem',
                      }}
                    >
                      {displayName}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {experts.length} {experts.length === 1 ? 'Expert' : 'Experts'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Desktop version - table */}
              <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {DEVELOPER_MODE && (
                        <TableCell
                          width="5%"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                            borderBottom: `2px solid ${theme.palette.divider}`,
                            py: 2,
                            background: alpha(theme.palette.background.default, 0.5),
                          }}
                        >
                          ID
                        </TableCell>
                      )}
                      <TableCell
                        width="25%"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.secondary,
                          borderBottom: `2px solid ${theme.palette.divider}`,
                          py: 2,
                          background: alpha(theme.palette.background.default, 0.5),
                        }}
                      >
                        Expert
                      </TableCell>
                      <TableCell
                        width="25%"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.secondary,
                          borderBottom: `2px solid ${theme.palette.divider}`,
                          py: 2,
                          background: alpha(theme.palette.background.default, 0.5),
                        }}
                      >
                        Position
                      </TableCell>
                      <TableCell
                        width="10%"
                        align="center"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.secondary,
                          borderBottom: `2px solid ${theme.palette.divider}`,
                          py: 2,
                          background: alpha(theme.palette.background.default, 0.5),
                        }}
                      >
                        PoC
                      </TableCell>
                      <TableCell
                        width="10%"
                        align="center"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.secondary,
                          borderBottom: `2px solid ${theme.palette.divider}`,
                          py: 2,
                          background: alpha(theme.palette.background.default, 0.5),
                        }}
                      >
                        Prompt
                      </TableCell>
                      <TableCell
                        width="10%"
                        align="center"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.secondary,
                          borderBottom: `2px solid ${theme.palette.divider}`,
                          py: 2,
                          background: alpha(theme.palette.background.default, 0.5),
                        }}
                      >
                        Status
                      </TableCell>
                      <TableCell
                        width="15%"
                        align="center"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.secondary,
                          borderBottom: `2px solid ${theme.palette.divider}`,
                          py: 2,
                          background: alpha(theme.palette.background.default, 0.5),
                          pr: 3,
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {experts.map((expert) => (
                      <TableRow
                        key={expert.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.03),
                          },
                          transition: 'background-color 0.2s',
                          '&:last-child td': {
                            borderBottom: 0,
                            pb: 0,
                          },
                        }}
                      >
                        {DEVELOPER_MODE && (
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                              {expert.id}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell sx={{ py: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              src={expert.imageURL}
                              alt={expert.name}
                              sx={{
                                width: 40,
                                height: 40,
                                border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(
                                  theme.palette.primary.main,
                                  0.05
                                )} 100%)`,
                              }}
                            />
                            <Box>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 600,
                                  color: theme.palette.text.primary,
                                  mb: 0.5,
                                }}
                              >
                                {expert.name}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.primary,
                              fontWeight: 500,
                            }}
                          >
                            {expert.position}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2 }}>
                          {booleanStatusIcon(parsePointsOfContact(expert.pointOfContactJson).length > 0 ? 1 : 0)}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2 }}>
                          {booleanStatusIcon(expert.promptModify ? 1 : 0)}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2 }}>
                          {statusIcon(expert.isActive !== undefined && expert.isActive !== null ? expert.isActive : 0)}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2, pr: 3 }}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              fullWidth
                              onClick={() => handleEditClick(expert)}
                              size="small"
                              sx={{
                                color: theme.palette.primary.main,
                                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                '&:hover': {
                                  color: 'white',
                                  backgroundColor: alpha(theme.palette.primary.main, 0.9),
                                },
                              }}
                              startIcon={<Edit fontSize="small" />}
                            >
                              Edit
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Mobile version - cards */}
              <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', p: 1, gap: 2 }}>
                {experts.map((expert) => (
                  <Paper
                    key={expert.id}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar
                        src={expert.imageURL}
                        alt={expert.name}
                        sx={{
                          width: 50,
                          height: 50,
                          border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(
                            theme.palette.primary.main,
                            0.05
                          )} 100%)`,
                        }}
                      />
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {expert.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                          }}
                        >
                          {expert.position}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                          PoC
                        </Typography>
                        {booleanStatusIcon(parsePointsOfContact(expert.pointOfContactJson).length > 0 ? 1 : 0)}
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                          Prompt
                        </Typography>
                        {booleanStatusIcon(expert.promptModify ? 1 : 0)}
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                          Status
                        </Typography>
                        {statusIcon(expert.isActive !== undefined && expert.isActive !== null ? expert.isActive : 0)}
                      </Box>
                    </Box>

                    <Button
                      fullWidth
                      onClick={() => handleEditClick(expert)}
                      size="small"
                      sx={{
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                        '&:hover': {
                          color: 'white',
                          backgroundColor: alpha(theme.palette.primary.main, 0.9),
                        },
                        py: 1,
                      }}
                      startIcon={<Edit fontSize="small" />}
                    >
                      Edit Expert
                    </Button>
                  </Paper>
                ))}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      <EditExpertModal open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} expert={currentExpert || {}} onSave={handleSaveClick} />
      <AddExpertModal open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} />

      <FilterDrawer />
    </PageTemplate>
  );
};

Experts.propTypes = {
  error: PropTypes.object,
};

export default Experts;
